// c61_666 — in-app workspace tabs (QBO-style): every open page is a tab under the hub bar, so the
// operator switches between Payouts, Purchases and the Ledger without browser tabs. Module store +
// localStorage (instant) + server ui_prefs.open_tabs (follows the user across browsers/deploys).
import { useSyncExternalStore } from "react";
import { HUBS } from "./hubs";
import { saveUiPref } from "./uiPrefs";

export const TABS_EVENT = "ss-workspace-tabs";
export const TABS_KEY = "ss_workspace_tabs";
export const MAX_TABS = 16;
// c61_738 — the last few closed pages, reopenable from the "+" menu
export const MAX_RECENT = 5;
const DEFAULT_TITLE = "Sales Snagger";

const uid = () => Math.random().toString(36).slice(2, 9) + Date.now().toString(36).slice(-3);

export const hubForPath = (pathname) => {
  const p = pathname || "/";
  if (p.startsWith("/reprice")) return "reprice";
  if (p.startsWith("/restock")) return "sales";
  if (p.startsWith("/connection-hub")) return "connection_hub";
  if (p.startsWith("/channel-hub")) return "channel_hub";
  if (p.startsWith("/books")) return "books_hub";
  if (p.startsWith("/ftmd")) return "ftmd";
  if (p.startsWith("/personal")) return "personal";
  if (p.startsWith("/ads")) return "ads";
  if (p.startsWith("/dropship") || p.startsWith("/warehouse-performance")) return "dropship";
  if (p.startsWith("/admin")) return "__admin";
  if (p.startsWith("/partners")) return "__partners";
  if (p.startsWith("/settings")) return "__settings";
  if (p.startsWith("/feedback")) return "__feedback";
  return "dashboard";
};

const humanize = (s) => String(s || "").replace(/[-_]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

/** Title from the live document title (pages call useDocTitle), else hub + section from the URL. */
export const titleFor = (pathname, search) => {
  const doc = (document.title || "").replace(/\s+—\s+Snagger$/, "").trim();
  if (doc && doc !== DEFAULT_TITLE) return doc;
  const hub = hubForPath(pathname);
  const label = { __admin: "User Management", __partners: "Partner Portal", __settings: "Data Backup", __feedback: "Feedback" }[hub]
    || `${HUBS.find((h) => h.id === hub)?.label || "Business"} Hub`;
  const sp = new URLSearchParams(search || "");
  const seg = (pathname || "").split("/").filter(Boolean).slice(1).map(humanize).join(" · ");
  const q = ["view", "sub", "tab", "section"].map((k) => sp.get(k)).filter(Boolean).map(humanize).join(" · ");
  const detail = seg || q;
  return detail ? `${detail} · ${label}` : label;
};

const empty = () => ({ tabs: [], active: null, recent: [] });
let state = empty();
try {
  const saved = JSON.parse(localStorage.getItem(TABS_KEY) || "null");
  if (saved && Array.isArray(saved.tabs)) {
    state = { tabs: saved.tabs.filter((t) => t && t.path), active: saved.active || null,
              recent: Array.isArray(saved.recent) ? saved.recent.filter((t) => t && t.path).slice(0, MAX_RECENT) : [] };
  }
} catch { /* fresh */ }

export const getTabsState = () => state;

/** Live {tabs, active} for components — read straight from the store during render (useSyncExternalStore), so a
 *  tree that was hidden (React Activity pauses effects) paints the current strip the moment it is shown again. */
const subscribe = (cb) => { window.addEventListener(TABS_EVENT, cb); return () => window.removeEventListener(TABS_EVENT, cb); };
export const useTabsState = () => useSyncExternalStore(subscribe, getTabsState, getTabsState);

const commit = (next, { sync = true } = {}) => {
  state = { recent: state.recent || [], ...next };
  try { localStorage.setItem(TABS_KEY, JSON.stringify(state)); } catch { /* quota */ }
  if (sync) saveUiPref({ open_tabs: state.tabs.map(({ id, path, search, title, hub, pinned }) => ({ id, path, search, title, hub, pinned: !!pinned })) });
  window.dispatchEvent(new CustomEvent(TABS_EVENT));
};

const sameUrl = (a, b) => a.path === b.path && (a.search || "") === (b.search || "");

/** c61_738 — closed tabs go to the front of the recently-closed list (newest first, one row per address, ≤ MAX_RECENT). */
const remember = (closed, { sync = true } = {}) => {
  const gone = (closed || []).filter((t) => t && t.path);
  if (!gone.length) return;
  const now = new Date().toISOString();
  const fresh = gone.map(({ path, search, title, hub }) => ({ id: uid(), path, search: search || "", title, hub, closed_at: now })).reverse();
  const rest = (state.recent || []).filter((r) => !fresh.some((f) => sameUrl(f, r)));
  const recent = [...fresh, ...rest].slice(0, MAX_RECENT);
  state = { ...state, recent };
  try { localStorage.setItem(TABS_KEY, JSON.stringify(state)); } catch { /* quota */ }
  if (sync) saveUiPref({ recent_tabs: recent.map(({ id, path, search, title, hub, closed_at }) => ({ id, path, search, title, hub, closed_at })) });
  window.dispatchEvent(new CustomEvent(TABS_EVENT));
};

/** Server copy at sign-in: only when this browser has no recently-closed pages of its own. */
export const adoptServerRecent = (list) => {
  if (!Array.isArray(list) || !list.length || (state.recent || []).length) return;
  state = { ...state, recent: list.filter((t) => t && t.path).map((t) => ({ ...t, id: t.id || uid() })).slice(0, MAX_RECENT) };
  try { localStorage.setItem(TABS_KEY, JSON.stringify(state)); } catch { /* quota */ }
  window.dispatchEvent(new CustomEvent(TABS_EVENT));
};

/** Reopen a recently-closed page in a new tab (and drop it from the list). Returns the URL to navigate to. */
export const reopenRecent = (id) => {
  const r = (state.recent || []).find((x) => x.id === id);
  if (!r) return null;
  const recent = state.recent.filter((x) => x.id !== id);
  state = { ...state, recent };
  saveUiPref({ recent_tabs: recent });
  const open = state.tabs.find((t) => sameUrl(t, r));
  return open ? activateTab(open.id) : openTab(r.path, r.search);
};

/** Server copy at sign-in: adopt it only when this browser has no tabs of its own yet. The tab for the page we are
 *  on right now always survives (matched by URL or appended) and stays active, so the strip never names another page. */
export const adoptServerTabs = (tabs) => {
  if (!Array.isArray(tabs) || !tabs.length || state.tabs.length > 1) return;
  const cleaned = tabs.filter((t) => t && t.path).map((t) => ({ ...t, id: t.id || uid() }))
    .filter((t, i, arr) => arr.findIndex((o) => o.id === t.id) === i);
  if (!cleaned.length) return;
  const cur = state.tabs.find((t) => t.id === state.active);
  const same = cur && cleaned.find((t) => t.path === cur.path && (t.search || "") === (cur.search || ""));
  let merged = cleaned;
  let active = cleaned[0].id;
  if (same) {  // keep the CURRENT tab's id so its mounted page (WorkspaceTabRoutes) survives the adoption
    merged = cleaned.map((t) => (t === same ? { ...t, id: cur.id } : t)).filter((t, i, arr) => arr.findIndex((o) => o.id === t.id) === i);
    active = cur.id;
  } else if (cur) { merged = [...cleaned.filter((t) => t.id !== cur.id), cur]; active = cur.id; }
  commit({ tabs: trim(merged, active), active }, { sync: false });
};
// c61_867 — tabs restored from localStorage / the server only survive for hubs this account may open
// (an FTMD-only bookkeeper must not inherit a Books Hub tab from an earlier, broader session).
const hubAllowed = (hub, user) => {
  if (user.role === "admin") return true;
  if (hub === "__admin") return false;
  if (!hub || hub.startsWith("__")) return true;
  const perms = user.permissions || [];
  return perms.includes(hub) || (hub === "connection_hub" && perms.includes("channel_hub"));
};
export const pruneTabsForUser = (user) => {
  if (!user?.username) return;
  const keep = state.tabs.filter((t) => hubAllowed(t.hub || hubForPath(t.path), user));
  if (keep.length === state.tabs.length) return;
  if (!keep.length) {
    const fresh = { id: uid(), path: "/", search: "", hub: "dashboard", title: "Business Hub", pinned: false };
    commit({ tabs: [fresh], active: fresh.id });
    return;
  }
  commit({ ...state, tabs: keep, active: keep.some((t) => t.id === state.active) ? state.active : keep[0].id });
};



const trim = (tabs, keepId) => {
  const out = [...tabs];
  while (out.length > MAX_TABS) {
    const i = out.findIndex((t) => !t.pinned && t.id !== keepId);
    if (i === -1) break;
    out.splice(i, 1);
  }
  return out;
};

/** The router moved (link click, sidebar, back button…): the ACTIVE tab follows, like a browser tab. */
export const syncLocation = (pathname, search) => {
  const url = { path: pathname || "/", search: search || "" };
  const cur = state.tabs.find((t) => t.id === state.active);
  if (cur && cur.path === url.path && cur.search === url.search) return;
  const tab = { ...url, hub: hubForPath(url.path), title: titleFor(url.path, url.search) };
  if (cur) {
    commit({ ...state, tabs: state.tabs.map((t) => (t.id === cur.id ? { ...t, ...tab } : t)) });
  } else {
    const fresh = { id: uid(), ...tab, pinned: false };
    commit({ tabs: trim([...state.tabs, fresh], fresh.id), active: fresh.id });
  }
};

/** The page announced its title (useDocTitle) — stamp it on the active tab. */
export const syncTitle = () => {
  const cur = state.tabs.find((t) => t.id === state.active);
  if (!cur) return;
  const doc = (document.title || "").replace(/\s+—\s+Snagger$/, "").trim();
  if (!doc || doc === DEFAULT_TITLE || doc === cur.title) return;
  commit({ ...state, tabs: state.tabs.map((t) => (t.id === cur.id ? { ...t, title: doc } : t)) });
};

export const activateTab = (id) => {
  const tab = state.tabs.find((t) => t.id === id);
  if (!tab) return null;
  if (state.active !== id) commit({ ...state, active: id }, { sync: false });
  return `${tab.path}${tab.search || ""}`;
};

/** New tab with the given URL (default: a copy of the current page). Returns the URL to navigate to. */
export const openTab = (path, search = "", { activate = true } = {}) => {
  const fresh = { id: uid(), path: path || "/", search: search || "", hub: hubForPath(path || "/"), title: titleFor(path || "/", search), pinned: false };
  const idx = state.tabs.findIndex((t) => t.id === state.active);
  const tabs = [...state.tabs];
  tabs.splice(idx === -1 ? tabs.length : idx + 1, 0, fresh);
  commit({ tabs: trim(tabs, fresh.id), active: activate ? fresh.id : state.active });
  return `${fresh.path}${fresh.search}`;
};

/** Close one tab. Returns the URL to navigate to when the active tab went away (else null). */
export const closeTab = (id) => {
  const idx = state.tabs.findIndex((t) => t.id === id);
  if (idx === -1) return null;
  remember([state.tabs[idx]]);
  const tabs = state.tabs.filter((t) => t.id !== id);
  if (state.active !== id) { commit({ ...state, tabs }); return null; }
  if (!tabs.length) {
    const fresh = { id: uid(), path: "/", search: "", hub: "dashboard", title: "Business Hub", pinned: false };
    commit({ tabs: [fresh], active: fresh.id });
    return "/";
  }
  const next = tabs[Math.min(idx, tabs.length - 1)];
  commit({ tabs, active: next.id });
  return `${next.path}${next.search || ""}`;
};

export const closeOtherTabs = (id) => {
  const keep = state.tabs.filter((t) => t.id === id || t.pinned);
  remember(state.tabs.filter((t) => !keep.includes(t)));
  commit({ tabs: keep, active: id });
  const cur = keep.find((t) => t.id === id);
  return cur ? `${cur.path}${cur.search || ""}` : null;
};

export const closeTabsToRight = (id) => {
  const idx = state.tabs.findIndex((t) => t.id === id);
  if (idx === -1) return null;
  const tabs = state.tabs.filter((t, i) => i <= idx || t.pinned);
  remember(state.tabs.filter((t) => !tabs.includes(t)));
  const activeGone = !tabs.some((t) => t.id === state.active);
  commit({ tabs, active: activeGone ? id : state.active });
  const cur = tabs.find((t) => t.id === id);
  return activeGone && cur ? `${cur.path}${cur.search || ""}` : null;
};

export const togglePinTab = (id) => {
  const tabs = state.tabs.map((t) => (t.id === id ? { ...t, pinned: !t.pinned } : t));
  tabs.sort((a, b) => Number(!!b.pinned) - Number(!!a.pinned));
  commit({ ...state, tabs });
};

export const moveTab = (fromId, toId) => {
  if (!fromId || !toId || fromId === toId) return;
  const tabs = [...state.tabs];
  const from = tabs.findIndex((t) => t.id === fromId);
  const to = tabs.findIndex((t) => t.id === toId);
  if (from === -1 || to === -1) return;
  tabs.splice(to, 0, tabs.splice(from, 1)[0]);
  commit({ ...state, tabs });
};
